import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { addToast } from '@/app/slices/toast';
import { WithLocalStorage } from '@/components';
import { ShipmentsGrid } from '@/components/common/shipments-grid';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { ShipmentStatus } from '@/pages/shipping/utils/enums';
import { PrimaryContext } from '@/services';
import {
  SemenDto,
  ShipmentDto,
  useAddSemenToShipment,
  useRemoveSemenFromShipment,
} from '@/services/semen-service';
import { shipmentApi } from '@/services/shipment-service/api';
import { useContextMenu } from '@/utils/context-menu';

import { useSemenAppContext } from '../../context/SemenAppContext';
import { useGridRefresh, useGridSelection } from '../../hooks';
import {
  applySemenTypeLabelToColumnDefs,
  semenAppShipmentSideBar,
  shipmentTabSemenColumnDefs,
} from '../../util/column-defs';
import { ShipmentDetailsPanel } from './ShipmentDetailsPanel';
import { StatsPanel } from './StatsPanel';

const LOCKED_SHIPMENT_STATUSES = [
  ShipmentStatus.IN_PROGRESS,
  ShipmentStatus.TRANSFER_TRIP,
  ShipmentStatus.SHIPPED,
];

type ShippingIntegrationTabProps = {
  isActive?: boolean;
};

export const ShippingIntegrationTab = ({
  isActive = true,
}: ShippingIntegrationTabProps = {}) => {
  const defaultGridOptions = useDefaultGridOptions();
  const { t } = useTranslation();
  const { themeMode } = useContext(PrimaryContext);
  const isSelectedSemenShippingEnabled = useFeatureFlag(
    'Selected_Semen_Shipping_Integration',
  );
  const isPersistSemenAcrossTabsEnabled = useFeatureFlag(
    'Open_New_Shipment_in_New_Window',
  );
  const shouldSyncSelectionToContext =
    !isSelectedSemenShippingEnabled || isPersistSemenAcrossTabsEnabled;
  const isSemenTypeLabelEnabled = useFeatureFlag('Semen_Type_Field_Label');
  const isSemenConventionalTypeEnabled = useFeatureFlag(
    'Semen_Conventional_Type',
  );
  const filteredShipmentTabSemenColumnDefs = applySemenTypeLabelToColumnDefs(
    isSemenTypeLabelEnabled
      ? shipmentTabSemenColumnDefs.filter((col: any) => col.field !== 'sexed')
      : shipmentTabSemenColumnDefs,
    isSemenTypeLabelEnabled,
    isSemenConventionalTypeEnabled,
  );
  const context = useSemenAppContext();
  const {
    setLoading,
    selectedSemen: contextSelectedSemen,
    setSelectedSemen: setContextSelectedSemen,
    shippingSemen: contextShippingSemen,
    setShippingSemen: setContextShippingSemen,
  } = context;

  // When the persistence flag is on, the accumulating shipping cart
  // (contextShippingSemen) is the source of truth for the "selected semen for
  // shipping" list; otherwise fall back to the existing selection behavior.
  const cartSemen = isPersistSemenAcrossTabsEnabled
    ? contextShippingSemen
    : contextSelectedSemen;
  const setCartSemen = isPersistSemenAcrossTabsEnabled
    ? setContextShippingSemen
    : setContextSelectedSemen;

  const location = useLocation();
  const semenGridRowData = useMemo(() => {
    if (isPersistSemenAcrossTabsEnabled) {
      return contextShippingSemen ?? [];
    }

    if (isSelectedSemenShippingEnabled) {
      return (
        ((location.state as any)?.shippingSemen as SemenDto[] | undefined) ?? []
      );
    }

    return contextSelectedSemen ?? [];
  }, [
    contextSelectedSemen,
    contextShippingSemen,
    isPersistSemenAcrossTabsEnabled,
    isSelectedSemenShippingEnabled,
    location.state,
  ]);
  const { getContextMenuItems: semenContextMenu } = useContextMenu({
    idKey: 'id',
  });
  const dispatch = useDispatch();

  const shipmentsGridRef = useRef<AgGridReact>(null);
  const semenGridRef = useRef<AgGridReact>(null);
  const [orderByChanged, setOrderByChanged] = useState<number>(0);
  const [selectedShipment, setSelectedShipment] = useState<ShipmentDto | null>(
    null,
  );
  const [selectedSemen, setSelectedSemen] = useState<SemenDto[]>([]);
  const [uniqueSireNames, setUniqueSireNames] = useState<string[]>([]);
  const [shipmentSearchInput, setShipmentSearchInput] = useState<string>('');
  const [shipmentSearchQuery, setShipmentSearchQuery] = useState<string>('');

  const totalUnitsToAdd = useMemo(
    () =>
      semenGridRowData.reduce(
        (sum, s) => sum + (Number(s.numberOfStrawsToAdd) || 0),
        0,
      ),
    [semenGridRowData],
  );

  const [showDonorScheduleConfirmDialog, setShowDonorScheduleConfirmDialog] =
    useState(false);
  const [showForceAddDialog, setShowForceAddDialog] = useState(false);
  const [forceAddWarningMessage, setForceAddWarningMessage] =
    useState<string>('');
  const [showOnlyUnassigned, setShowOnlyUnassigned] = useState<boolean>(true);

  const { mutateAsync: addSemenToShipment } = useAddSemenToShipment();
  const { mutateAsync: removeSemenFromShipment } = useRemoveSemenFromShipment();

  const isShipmentLocked = useMemo(() => {
    if (!selectedShipment?.shipmentStatus) return false;
    return LOCKED_SHIPMENT_STATUSES.includes(selectedShipment.shipmentStatus);
  }, [selectedShipment?.shipmentStatus]);

  const syncSemenGridRowData = useCallback(() => {
    const api = semenGridRef.current?.api;
    if (!api) return;
    api.setGridOption('rowData', semenGridRowData);
    api.refreshCells({ force: true });
    api.redrawRows();
  }, [semenGridRowData]);

  const refreshSemenGrid = useCallback(() => {
    semenGridRef.current?.api?.refreshCells({ force: true });
  }, []);

  // Keep-alive: when the shipping tab becomes active, force the semen grid to
  // reflect the current accumulating cart (its rowData may have changed while the
  // tab was hidden by keep-alive, e.g. after "Add to Shipment" on the Home tab).
  useEffect(() => {
    if (!isPersistSemenAcrossTabsEnabled || !isActive) return;
    syncSemenGridRowData();
  }, [isActive, isPersistSemenAcrossTabsEnabled, syncSemenGridRowData]);

  useGridRefresh({
    gridRefs: [shipmentsGridRef],
    routeName: 'shipping-integration',
    onRefresh: () => {
      shipmentsGridRef.current?.api?.refreshServerSide({ purge: true });
      refreshSemenGrid();
    },
    dependencies: [orderByChanged, showOnlyUnassigned],
  });

  const handleSemenGridReady = useCallback(() => {
    if (isPersistSemenAcrossTabsEnabled) {
      syncSemenGridRowData();
      return;
    }
    refreshSemenGrid();
  }, [isPersistSemenAcrossTabsEnabled, refreshSemenGrid, syncSemenGridRowData]);

  const { handleSelectionChanged: onSemenRowSelectionChanged } =
    useGridSelection<SemenDto>({
      gridRef: semenGridRef,
      onSelectionChange: setSelectedSemen,
      mode: 'multiple',
    });

  useEffect(() => {
    if (semenGridRowData.length > 0) {
      const uniqueSires = Array.from(
        new Set(
          semenGridRowData.map((semen) => semen?.sire?.name).filter(Boolean),
        ),
      ) as string[];
      setUniqueSireNames(uniqueSires);
    } else {
      setUniqueSireNames([]);
    }
  }, [semenGridRowData]);

  const handleAddToShipment = async () => {
    try {
      const hasZeroStraws = selectedSemen.some(
        (semen) => semen.numberOfStrawsToAdd === 0,
      );

      if (hasZeroStraws) {
        dispatch(
          addToast({
            severity: 'warn',
            summary: t('common.validation'),
            detail: t('common.error'),
            life: ToastLife.DEFAULT,
          }),
        );
        return;
      }

      const hasDonorSchedule = selectedSemen.some(
        (semen) =>
          semen.donorScheduleId !== null &&
          semen.donorScheduleId !== undefined &&
          semen.donorScheduleId.trim() !== '',
      );

      if (hasDonorSchedule) {
        setShowDonorScheduleConfirmDialog(true);
        return;
      }

      await proceedWithAddToShipment();
    } catch (error) {
      console.error('Error adding semen to shipment:', error);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail:
            error instanceof Error
              ? error.message
              : t('common.anErrorOccurred'),
          life: ToastLife.DEFAULT,
        }),
      );
    }
  };

  const proceedWithAddToShipment = async (force?: boolean) => {
    const res = await addSemenToShipment({
      shipmentId: selectedShipment?.id as string,
      semenList: selectedSemen.map((semen) => ({
        numberOfStraws:
          semen.numberOfStrawsToAdd ?? Number(semen.quantity) ?? 0,
        semenId: semen.id,
      })),
      force,
    });

    const success = res.success ?? false;
    const message = res.message ?? 'Operation completed';

    const isAlreadyAssigned = message
      .toLowerCase()
      .includes('already assigned to another shipment');

    if (isAlreadyAssigned && !force) {
      setForceAddWarningMessage(message);
      setShowForceAddDialog(true);
      return;
    }
    const isWarning = message.toLowerCase().includes('warning');
    const severity = isWarning ? 'warn' : success ? 'success' : 'warn';
    const summary =
      isWarning || !success ? t('common.warning') : t('common.success');
    const life = isWarning || !success ? ToastLife.LONG : ToastLife.DEFAULT;

    dispatch(
      addToast({
        severity,
        summary,
        detail: message,
        life,
      }),
    );

    refreshSemenGrid();
    shipmentsGridRef.current?.api?.refreshServerSide({ purge: true });

    if (selectedShipment?.id) {
      const searchParams = new URLSearchParams();
      if (selectedShipment.shipmentNumber)
        searchParams.set('shipmentNumber', selectedShipment.shipmentNumber);
      const updatedShipment = (await shipmentApi.getShipments(
        searchParams,
      )) as unknown as { items: ShipmentDto[] };
      if (updatedShipment.items.length > 0) {
        setSelectedShipment(updatedShipment.items[0]);
      }
    }
    if (!isWarning) {
      const addedIds = new Set(selectedSemen.map((s) => s.id));
      if (shouldSyncSelectionToContext) {
        setCartSemen((cartSemen ?? []).filter((s) => !addedIds.has(s.id)));
      }
      setSelectedSemen([]);
      semenGridRef.current?.api?.deselectAll();
    }
  };

  const handleRemoveSemenFromShipment = async (semenId: string) => {
    if (!selectedShipment?.id) return;

    try {
      const res = await removeSemenFromShipment({
        shipmentId: selectedShipment.id,
        semenIds: [semenId],
      });

      const success = res.success ?? false;
      const message = res.message ?? 'Operation completed';

      dispatch(
        addToast({
          severity: success ? 'success' : 'error',
          summary: success ? t('common.success') : t('common.error'),
          detail: message,
          life: ToastLife.DEFAULT,
        }),
      );

      refreshSemenGrid();
      shipmentsGridRef.current?.api?.refreshServerSide({ purge: true });

      const searchParams = new URLSearchParams();
      if (selectedShipment.shipmentNumber)
        searchParams.set('shipmentNumber', selectedShipment.shipmentNumber);
      const updatedShipment = (await shipmentApi.getShipments(
        searchParams,
      )) as unknown as { items: ShipmentDto[] };
      if (updatedShipment.items.length > 0) {
        const updatedShipmentData = updatedShipment.items[0];
        setSelectedShipment(updatedShipmentData);

        if ('semens' in updatedShipmentData && updatedShipmentData.semens) {
          const uniqueSires = Array.from(
            new Set(
              (updatedShipmentData.semens as any[])
                .map((semen: any) => semen.sireName)
                .filter(Boolean),
            ),
          ) as string[];
          setUniqueSireNames(uniqueSires);
        }
      }
    } catch (error) {
      console.error('Error removing semen from shipment:', error);
    }
  };
  const onSemenCellValueChanged = (event: any) => {
    if (event.colDef?.field !== 'numberOfStrawsToAdd') {
      return;
    }

    const semen = event.data;
    if (semen?.id) {
      const newValue = Number(event.value) ?? 0;
      const quantity = Number(semen.quantity) || 0;

      if (quantity === 0) {
        if (newValue !== 0) {
          event.node.setDataValue(event.colDef.field, null);

          setSelectedSemen(
            selectedSemen.map((s) =>
              s.id === semen.id
                ? { ...s, numberOfStrawsToAdd: null as any }
                : s,
            ),
          );
          if (shouldSyncSelectionToContext) {
            setCartSemen(
              (cartSemen ?? []).map((s) =>
                s.id === semen.id
                  ? { ...s, numberOfStrawsToAdd: null as any }
                  : s,
              ),
            );
          }
          dispatch(
            addToast({
              severity: 'warn',
              summary: t('common.validation'),
              detail: t('common.error'),
              life: ToastLife.DEFAULT,
            }),
          );
        }
        return;
      }

      if (newValue > quantity) {
        if (newValue !== null) {
          event.node.setDataValue(event.colDef.field, null);
          setSelectedSemen(
            selectedSemen.map((s) =>
              s.id === semen.id
                ? { ...s, numberOfStrawsToAdd: null as any }
                : s,
            ),
          );
          if (shouldSyncSelectionToContext) {
            setCartSemen(
              (cartSemen ?? []).map((s) =>
                s.id === semen.id
                  ? { ...s, numberOfStrawsToAdd: null as any }
                  : s,
              ),
            );
          }
          dispatch(
            addToast({
              severity: 'warn',
              summary: t('common.validation'),
              detail: t('common.error'),
              life: ToastLife.DEFAULT,
            }),
          );
        }
        return;
      }

      setSelectedSemen(
        selectedSemen.map((s) =>
          s.id === semen.id ? { ...s, numberOfStrawsToAdd: newValue } : s,
        ),
      );
      if (shouldSyncSelectionToContext) {
        setCartSemen(
          (cartSemen ?? []).map((s) =>
            s.id === semen.id ? { ...s, numberOfStrawsToAdd: newValue } : s,
          ),
        );
      }
    }
  };

  const handleClearSelection = () => {
    setSelectedSemen([]);
    if (shouldSyncSelectionToContext) {
      setCartSemen([]);
    }
    semenGridRef.current?.api?.deselectAll();
    semenGridRef.current?.api?.refreshCells({ force: true });
  };

  const handleShipmentSearch = () => {
    setShipmentSearchQuery(shipmentSearchInput);
  };

  return (
    <div className="flex h-full min-h-0 flex-col p-4">
      <div className="grid min-h-0 grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <div className="flex min-h-0 flex-col">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('shipping.selectedSemenForShipping')}
              </h3>
              <div className="flex items-center justify-end gap-4">
                <Button
                  label={t('common.clear')}
                  icon="pi pi-times"
                  size="small"
                  text
                  onClick={handleClearSelection}
                />
                <Button
                  label={t('shipping.addToShipment')}
                  icon="pi pi-arrow-right"
                  size="small"
                  disabled={
                    !selectedSemen.length ||
                    !selectedShipment?.id ||
                    isShipmentLocked
                  }
                  onClick={handleAddToShipment}
                  tooltip={
                    isShipmentLocked
                      ? t('shipping.cannotAddToLockedShipment')
                      : t('shipping.addToShipment')
                  }
                />
              </div>
            </div>
            <div className="h-[250px] min-h-0">
              <WithLocalStorage storageKey="shipping-semen-grid">
                <AgGridReact
                  gridOptions={defaultGridOptions}
                  ref={semenGridRef}
                  className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-full`}
                  columnDefs={filteredShipmentTabSemenColumnDefs}
                  rowData={semenGridRowData}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  defaultColDef={defaultColDef}
                  sideBar={semenAppShipmentSideBar}
                  getContextMenuItems={semenContextMenu}
                  getRowId={(params) => String(params.data.id)}
                  onSelectionChanged={onSemenRowSelectionChanged}
                  onGridReady={handleSemenGridReady}
                  onFilterChanged={refreshSemenGrid}
                  onSortChanged={refreshSemenGrid}
                  onCellValueChanged={onSemenCellValueChanged}
                  rowModelType="clientSide"
                />
              </WithLocalStorage>
            </div>
          </div>

          <div className="flex min-h-0 flex-col">
            <ShipmentsGrid
                gridRef={shipmentsGridRef}
                selectedShipment={selectedShipment}
                setSelectedShipment={setSelectedShipment}
                filters={
                  shipmentSearchQuery ? { search: shipmentSearchQuery } : {}
                }
                storageKey="shipping-shipments-grid"
                title={t('shipping.strawShipments')}
                showSearch={true}
                searchInput={shipmentSearchInput}
                onSearchInputChange={setShipmentSearchInput}
                onSearch={handleShipmentSearch}
                openNewInNewWindow={isPersistSemenAcrossTabsEnabled}
              />
          </div>
        </div>

        <div className="space-y-4 xl:col-span-1">
          <StatsPanel
            selectedSemenCount={selectedSemen?.length || 0}
            uniqueSireNamesCount={uniqueSireNames.length}
            totalUnitsToAdd={totalUnitsToAdd}
          />
          <ShipmentDetailsPanel
            selectedShipment={selectedShipment}
            onRemoveSemenFromShipment={handleRemoveSemenFromShipment}
            isShipmentLocked={isShipmentLocked}
          />
        </div>
      </div>
      <ConfirmDialog
        visible={showDonorScheduleConfirmDialog}
        onHide={() => setShowDonorScheduleConfirmDialog(false)}
        message={t('common.doYouWantToProceed')}
        header={t('donorScheduling.soScheduleWarning')}
        icon="pi pi-exclamation-triangle"
        accept={async () => {
          setShowDonorScheduleConfirmDialog(false);
          await proceedWithAddToShipment();
        }}
        reject={() => setShowDonorScheduleConfirmDialog(false)}
        acceptLabel={t('common.yes')}
        rejectLabel={t('common.no')}
      />
      <ConfirmDialog
        visible={showForceAddDialog}
        onHide={() => setShowForceAddDialog(false)}
        message={`${forceAddWarningMessage} ${t('common.doYouWantToProceed')}`}
        header={t('common.warning')}
        icon="pi pi-exclamation-triangle"
        accept={async () => {
          setShowForceAddDialog(false);
          await proceedWithAddToShipment(true);
        }}
        reject={() => setShowForceAddDialog(false)}
        acceptLabel={t('common.yes')}
        rejectLabel={t('common.no')}
      />
    </div>
  );
};
